import React, { useState } from "react";
import { useTheme } from "../../hooks/useTheme";
import api from "../../api/api";
import {
    UserPlus, Mail, Send, CheckCircle2, XCircle, Plus, X, Users
} from "lucide-react";

export default function InviteCoaches() {
    const { isDarkMode } = useTheme();
    const [emails, setEmails] = useState([""]);
    const [personalMessage, setPersonalMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState(null);

    const showMessage = (text, type = "success") => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 5000);
    };

    const addEmailField = () => {
        if (emails.length < 10) {
            setEmails([...emails, ""]);
        }
    };

    const removeEmailField = (index) => {
        if (emails.length > 1) {
            setEmails(emails.filter((_, i) => i !== index));
        }
    };

    const updateEmail = (index, value) => {
        const updated = [...emails];
        updated[index] = value;
        setEmails(updated);
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const validEmails = emails.filter((e) => isValidEmail(e.trim()));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validEmails.length === 0) {
            showMessage("Please enter at least one valid email address.", "error");
            return;
        }
        setSending(true);
        try {
            await api.post("/coach/invite-coaches", {
                emails: validEmails,
                message: personalMessage.trim() || undefined,
            });
            showMessage(`Invitation${validEmails.length > 1 ? "s" : ""} sent successfully to ${validEmails.length} coach${validEmails.length > 1 ? "es" : ""}!`);
            setEmails([""]);
            setPersonalMessage("");
        } catch (err) {
            showMessage(err.response?.data?.error || "Failed to send invitations. Please try again.", "error");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
            <div className="max-w-3xl mx-auto px-4 py-8">
                <header className="mb-8">
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${isDarkMode ? "bg-primary-900/40" : "bg-primary-50"}`}>
                            <UserPlus className="text-primary-500" size={28} />
                        </div>
                        <div className="flex-1">
                            <h1 className={`text-3xl font-extrabold tracking-tight ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                Invite Coaches
                            </h1>
                            <p className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                Know a great coach? Invite them to join BizBrand and help others grow.
                            </p>
                        </div>
                    </div>
                </header>

                {message && (
                    <div role="alert" className={`mb-5 flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium transition-all ${
                        message.type === "error"
                            ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                            : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                    }`}>
                        {message.type === "error" ? <XCircle size={18} /> : <CheckCircle2 size={18} />}
                        {message.text}
                    </div>
                )}

                {/* Info Card */}
                <div className={`rounded-2xl border p-6 mb-6 ${isDarkMode ? "bg-slate-800/60 border-slate-700/60" : "bg-white border-slate-200"}`}>
                    <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isDarkMode ? "bg-primary-900/40" : "bg-primary-50"}`}>
                            <Users className="text-primary-500" size={24} />
                        </div>
                        <div>
                            <h3 className={`font-bold text-[15px] mb-1 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                Help grow the coaching community
                            </h3>
                            <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                Invite experienced professionals to become coaches on BizBrand. They'll receive an email invitation
                                with instructions to sign up and start coaching. You can invite up to 10 coaches at a time.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Invite Form */}
                <form onSubmit={handleSubmit} className={`rounded-2xl border p-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"} shadow-sm`}>
                    <div className="mb-6">
                        <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
                            <Mail size={14} className="inline mr-2 -mt-0.5" />
                            Email Addresses
                        </label>
                        <div className="space-y-3">
                            {emails.map((email, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => updateEmail(index, e.target.value)}
                                        placeholder="coach@example.com"
                                        className={`flex-1 px-4 py-2.5 rounded-xl text-sm border transition-colors ${
                                            isDarkMode
                                                ? "bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500 focus:border-primary-500"
                                                : "bg-slate-50 border-slate-300 text-slate-900 placeholder-slate-400 focus:border-primary-500"
                                        } focus:outline-none focus:ring-2 focus:ring-primary-500/20`}
                                    />
                                    {emails.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeEmailField(index)}
                                            className={`p-2 rounded-lg transition-colors ${
                                                isDarkMode
                                                    ? "text-slate-400 hover:text-red-400 hover:bg-red-900/20"
                                                    : "text-slate-400 hover:text-red-600 hover:bg-red-50"
                                            }`}
                                            aria-label="Remove email"
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        {emails.length < 10 && (
                            <button
                                type="button"
                                onClick={addEmailField}
                                className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                                    isDarkMode
                                        ? "text-primary-400 hover:bg-primary-900/20"
                                        : "text-primary-600 hover:bg-primary-50"
                                }`}
                            >
                                <Plus size={14} /> Add another email
                            </button>
                        )}
                    </div>

                    <div className="mb-6">
                        <label htmlFor="personal-message" className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
                            Personal Message <span className={`font-normal ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>(optional)</span>
                        </label>
                        <textarea
                            id="personal-message"
                            value={personalMessage}
                            onChange={(e) => setPersonalMessage(e.target.value)}
                            placeholder="Add a personal note to your invitation..."
                            rows={4}
                            maxLength={500}
                            className={`w-full px-4 py-3 rounded-xl text-sm border transition-colors resize-none ${
                                isDarkMode
                                    ? "bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500 focus:border-primary-500"
                                    : "bg-slate-50 border-slate-300 text-slate-900 placeholder-slate-400 focus:border-primary-500"
                            } focus:outline-none focus:ring-2 focus:ring-primary-500/20`}
                        />
                        <p className={`text-xs mt-1.5 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                            {personalMessage.length}/500 characters
                        </p>
                    </div>

                    <div className="flex items-center justify-between">
                        <p className={`text-xs ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                            {validEmails.length} valid email{validEmails.length !== 1 ? "s" : ""} to send
                        </p>
                        <button
                            type="submit"
                            disabled={sending || validEmails.length === 0}
                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-primary-600/20"
                        >
                            {sending ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <Send size={16} />
                                    Send Invitation{validEmails.length > 1 ? "s" : ""}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
